/**
 * Several styles side by side (JF 2026-10-13, "one customer's styles in one Excel for the first approval"): the rows of
 * all the cost sheets lined up so the same thing sits on the same row — main fabric, rib, main label, care label,
 * buttons, cutting, sewing… — with N/A where a style has none. Used by the group page and its Excel export.
 *
 * Fabrics and trims are simply listed in order — fabric 1, 2, 3…, trim 1, 2, 3… (JF 2026-10-13: "有什么面料就有什么面料",
 * matching by garment part is too fine and breaks with trousers). The CM and other costs line up by what they are:
 *   Print = the factory's embroidery / print;  VAP = washing and other special processes;  CM = cutting, sewing, packing
 *   (+ rows the AM added);  Other = customs & documents and the Additional costs Lever Style pays.
 */
import { actPrice, addRows, blankSheet, cmLines, cmPartsOf, coefOf, isSet, perPiece, quoPrice, setCmParts, TIER_PARTS, totals, type Totals } from "./costing.ts";
import { tierSheet } from "./tiers.ts";
import { n } from "./num.ts";
import type { CmPartKey, LineRow, Per, Sheet } from "./types.ts";

/** rows the "same amount for every style" action makes sense for: CM / other costs (fabric 2 of one style is not fabric 2 of another) */
export const rowIsItem = (key: string) => !/^(fab|trim):/.test(key);

export type RangeSection = "fab" | "trim" | "print" | "vap" | "cm" | "other";
export const RANGE_SECTIONS: [RangeSection, string, string][] = [
  ["fab", "Fabric", "面料"], ["trim", "Trims", "辅料"], ["print", "Print", "印花 / 绣花"],
  ["vap", "VAP", "洗水 / 特殊工艺"], ["cm", "CM", "CM 加工费"], ["other", "Other", "运输 / 报关等"],
];

/** Where a cell's numbers live on its sheet, for editing. */
export type RangeRef =
  | { kind: "fab" | "trim"; i: number }
  | { kind: "cm"; part: string }
  | { kind: "more"; i: number }
  | { kind: "add"; key: string };

export interface RangeCell {
  ref: RangeRef;
  /** what it is on this style (name · quality, or the CM / Additional description) */
  desc: string;
  /** fabric / trim lines: where on the garment */
  pos?: string;
  /** the amount as kept (USD) and as typed in RMB */
  px: string; cny?: string;
  /** fabric / trim lines */
  yld?: string; unit?: string;
  /** CM / Additional rows: per piece or whole order */
  per?: Per;
  /** per garment, USD */
  actual: number; quoted: number;
}
export interface RangeRow { key: string; section: RangeSection; en: string; zh: string; cells: (RangeCell | null)[] }

const norm = (v: unknown) => String(v ?? "").toLowerCase().replace(/[\s（）()·,，:：/_-]+/g, "");

const lineDesc = (r: LineRow) => [r.name, r.qual].filter((x) => String(x || "").trim()).join(" · ");

/** One style's cells, keyed: [key, section, label en, label zh, cell]. */
function cellsOf(s: Sheet): [string, RangeSection, string, string, RangeCell][] {
  const c = coefOf(s);
  const out: [string, RangeSection, string, string, RangeCell][] = [];
  const seen = new Map<string, number>();
  const uniq = (k: string) => { const i = (seen.get(k) || 0) + 1; seen.set(k, i); return i > 1 ? `${k}#${i}` : k; };
  // fabrics and trims in their order on the sheet: row k = the style's k-th line
  for (const kind of ["fab", "trim"] as const) {
    let k = 0;
    s[kind].forEach((r, i) => {
      if (!String(r.name || r.px || "").trim()) return;
      k++;
      out.push([`${kind}:${k}`, kind, `${kind === "fab" ? "Fabric" : "Trim"} ${k}`, `${kind === "fab" ? "面料" : "辅料"} ${k}`,
        { ref: { kind, i }, desc: lineDesc(r), pos: r.pos, px: r.px, cny: r.cny, yld: r.yld, unit: kind === "fab" ? r.unit : "pcs", actual: actPrice(r, c), quoted: quoPrice(r, kind, c) }]);
    });
  }
  const qty = n(s.h.qty);
  for (const l of cmLines(s, c)) {
    if (!isSet(l.px) && !(l.part === "docs")) continue;
    const sec: RangeSection = l.part === "emb" ? "print" : l.part === "wash" || l.part === "other" ? "vap" : l.part === "docs" ? "other" : "cm";
    const ref: RangeRef = l.part ? { kind: "cm", part: l.part } : { kind: "more", i: Number(l.key.replace("more", "")) };
    const key = l.part ? "cm:" + l.part : uniq("cm:more:" + norm(l.name));
    out.push([key, sec, l.part ? l.en : l.name || "CM", l.part ? l.zh : l.name || "CM",
      { ref, desc: l.desc, px: l.px, cny: l.cny, per: l.per, actual: l.actual, quoted: l.actual * l.factor }]);
  }
  for (const a of addRows(s, c)) {
    if (!n(a.amount)) continue;
    out.push(["add:" + a.key, "other", a.en, a.zh, { ref: { kind: "add", key: a.key }, desc: a.desc, px: a.amount, cny: a.cny, per: a.per, actual: perPiece(a.amount, a.per, qty), quoted: a.quoted }]);
  }
  return out;
}

/** The rows of several styles lined up; `null` = this style has nothing on that row (N/A). */
export function rangeRows(sheets: Sheet[]): RangeRow[] {
  const rows = new Map<string, RangeRow>();
  const order: string[] = [];
  sheets.forEach((s, col) => {
    for (const [key, section, en, zh, cell] of cellsOf(s)) {
      let r = rows.get(key);
      if (!r) { r = { key, section, en, zh, cells: sheets.map(() => null) }; rows.set(key, r); order.push(key); }
      r.cells[col] = cell;
    }
  });
  const rank = (k: RangeSection) => RANGE_SECTIONS.findIndex((x) => x[0] === k);
  const cmRank = (key: string) => ["cm:cut", "cm:sew", "cm:pack", "cm:cmt"].indexOf(key);
  return order.map((k) => rows.get(k)!).sort((a, b) => rank(a.section) - rank(b.section)
    || (a.section === "cm" && b.section === "cm" ? ((cmRank(a.key) + 9) % 9) - ((cmRank(b.key) + 9) % 9) : 0)
    || order.indexOf(a.key) - order.indexOf(b.key));
}

/** Per style: each section's actual cost per garment, and the sheet totals. */
export interface RangeTotals { bySection: Record<RangeSection, number>; totals: Totals; target: number }
export function rangeTotals(sheets: Sheet[], rows: RangeRow[] = rangeRows(sheets)): RangeTotals[] {
  return sheets.map((s, col) => {
    const bySection = Object.fromEntries(RANGE_SECTIONS.map(([k]) => [k, 0])) as Record<RangeSection, number>;
    for (const r of rows) { const c = r.cells[col]; if (c) bySection[r.section] += c.actual; }
    return { bySection, totals: totals(s), target: n(s.target) };
  });
}

/**
 * Change one cell of a style group on its own sheet (the group page writes straight into each style's cost sheet).
 * `money` = the amount as kept (USD px, plus the RMB figure when typed in RMB). Mutates.
 */
export function setRangeCell(s: Sheet, ref: RangeRef, f: "px" | "yld" | "per", v: { px: string; cny?: string } | string): void {
  const money = typeof v === "string" ? { px: v } : v;
  const put = (o: { px: string; cny?: string }) => { o.px = money.px; if (money.cny != null) o.cny = money.cny; else delete o.cny; };
  if (ref.kind === "fab" || ref.kind === "trim") {
    const r = s[ref.kind][ref.i];
    if (!r) return;
    if (f === "px") { put(r); r.src.px = { t: "man", d: "edited in the style group / 在款式组合里修改" }; }
    else if (f === "yld") { r.yld = String(v); r.src.yld = { t: "man", d: "edited in the style group / 在款式组合里修改" }; }
    return;
  }
  if (ref.kind === "add") {
    if (ref.key === "test" || ref.key === "insp" || ref.key === "setup") {
      const k = ref.key;
      if (f === "px") { s[k] = money.px; const e = { ...(s.extra?.[k] || {}) }; if (money.cny != null) e.cny = money.cny; else delete e.cny; s.extra = { ...(s.extra || {}), [k]: e }; }
      else if (f === "per") s.extra = { ...(s.extra || {}), [k]: { ...(s.extra?.[k] || {}), per: v as Per } };
      return;
    }
    const a = s.add[ref.key as keyof Sheet["add"]];
    if (!a) return;
    if (f === "px") put(a); else if (f === "per") a.per = v as Per;
    return;
  }
  const p = cmPartsOf(s);
  if (ref.kind === "cm") {
    const part = p[ref.part as CmPartKey];
    if (!part) return;
    if (f === "px") put(part); else if (f === "per") part.per = v as Per;
  } else {
    const list = [...(s.cm.more || [])];
    const m = list[ref.i];
    if (!m) return;
    const row = { ...m };
    if (f === "px") put(row); else if (f === "per") row.per = v as Per;
    list[ref.i] = row; s.cm.more = list;
  }
  setCmParts(s, p);
  if (f === "px") s.cm.src = { t: "man", d: "edited in the style group / 在款式组合里修改" };
}

/**
 * Which quantity each style is shown at in a group (JF 2026-10-13): "active" = the quantity each cost sheet shows,
 * a number = that quantity for every style (a style without it gets an empty "missing" column), "all" = every quantity
 * of a tiered quote side by side. The page and the Excel export use the same columns.
 */
export type RangeView = "active" | "all" | number;
export interface RangeColumn { s: number; qty: number; sheet: Sheet; missing: boolean; first: boolean; span: number }
export function rangeColumns(sheets: Sheet[], view: RangeView): RangeColumn[] {
  return sheets.flatMap((s, i): RangeColumn[] => {
    const qtys = s.tiers?.qtys.length ? s.tiers.qtys : null;
    if (view === "all" && qtys) return qtys.map((q, k) => ({ s: i, qty: q, sheet: tierSheet(s, q).sheet, missing: false, first: k === 0, span: qtys.length }));
    if (typeof view === "number") {
      if (qtys) return qtys.includes(view) ? [{ s: i, qty: view, sheet: tierSheet(s, view).sheet, missing: false, first: true, span: 1 }]
        : [{ s: i, qty: view, sheet: blankSheet(s.h.date), missing: true, first: true, span: 1 }];
      return n(s.h.qty) === view ? [{ s: i, qty: view, sheet: s, missing: false, first: true, span: 1 }]
        : [{ s: i, qty: view, sheet: blankSheet(s.h.date), missing: true, first: true, span: 1 }];
    }
    return [{ s: i, qty: n(s.h.qty), sheet: qtys ? tierSheet(s, s.tiers!.active ?? qtys[0]).sheet : s, missing: false, first: true, span: 1 }];
  });
}
/** every quantity the styles of a group are quoted at (tiered quotes and single quantities), sorted */
export const rangeQtys = (sheets: Sheet[]): number[] =>
  [...new Set(sheets.flatMap((s) => (s.tiers?.qtys.length ? s.tiers.qtys : n(s.h.qty) > 0 ? [n(s.h.qty)] : [])))].sort((a, b) => a - b);

/**
 * A change read back from the group's Excel, for one quantity of a style (JF 2026-10-14): the quantity its cost sheet
 * shows (or a style without a tiered quote) changes the sheet itself; another quantity of a tiered quote changes that
 * quantity's own numbers (line prices / yields, per-piece CM parts). Whole-order amounts and Additional costs are the same
 * for every quantity. Mutates.
 */
export function setTierCell(s: Sheet, qty: number, ref: RangeRef, f: "px" | "yld", v: { px: string; cny?: string } | string): void {
  const t = s.tiers;
  if (!t?.qtys.length || t.active === qty || !t.qtys.includes(qty)) { setRangeCell(s, ref, f, v); return; }
  const q = String(qty);
  const money = typeof v === "string" ? { px: v } : v;
  if (ref.kind === "fab" || ref.kind === "trim") {
    const r = s[ref.kind][ref.i];
    if (!r) return;
    const cur = { ...(r.tq?.[q] || { px: r.px, yld: r.yld, why: "", ...(isSet(r.cny) ? { cny: r.cny } : {}) }) };
    if (f === "px") { cur.px = money.px; if (money.cny != null) cur.cny = money.cny; else delete cur.cny; }
    else cur.yld = typeof v === "string" ? v : v.px;
    delete cur.none;
    r.tq = { ...(r.tq || {}), [q]: { ...cur, why: cur.why || "edited in Excel / 在 Excel 里修改" } };
    return;
  }
  if (ref.kind === "cm" && f === "px" && TIER_PARTS.includes(ref.part as CmPartKey)) {
    const part = ref.part as CmPartKey;
    const pr = { ...(t.proc?.[q] || {}) };
    pr[part] = money.px;
    const cny = { ...(pr.cny || {}) };
    if (money.cny != null) cny[part] = money.cny; else delete cny[part];
    pr.cny = cny;
    t.proc = { ...(t.proc || {}), [q]: pr };
    return;
  }
  setRangeCell(s, ref, f, v);
}
